import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Title, Paper, Badge, SimpleGrid, Text, Loader, Center, Tabs, Select, Box } from '@mantine/core';
import { IconArmchair, IconMovie, IconUser } from '@tabler/icons-react';
import { API_PYTHON_URL, API_BASE_URL } from '../config';
import SeatGrid from './SeatGrid';

function ReservationCheck() {
    const [mySeats, setMySeats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [username, setUsername] = useState('');

    // Status Tab State
    const [movies, setMovies] = useState([]);
    const [selectedMovie, setSelectedMovie] = useState(null);
    const [movieSeats, setMovieSeats] = useState([]);
    const [movieLoading, setMovieLoading] = useState(false);

    useEffect(() => {
        const storedUsername = localStorage.getItem('username');
        if (storedUsername) setUsername(storedUsername);

        // Fetch My Reservations
        const fetchMyReservations = async () => {
            if (!storedUsername) {
                setLoading(false);
                return;
            }
            try {
                const response = await axios.get(`${API_PYTHON_URL}/reservations/me`, {
                    params: { username: storedUsername }
                });
                setMySeats(response.data);
            } catch (error) {
                console.error(error);
                setMySeats([]);
            } finally {
                setLoading(false);
            }
        };

        // Fetch Movies List (from Java Backend)
        const fetchMovies = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/movies`);
                // Format for Mantine Select: { value: 'id', label: 'title' }
                const options = response.data.map(m => ({ value: m.id.toString(), label: m.title }));
                setMovies(options);
            } catch (error) {
                console.error("Failed to fetch movies", error);
            }
        };

        fetchMyReservations();
        fetchMovies();
    }, []);

    // Fetch Seats when movie selected
    useEffect(() => {
        if (!selectedMovie) return;
        setMovieLoading(true);
        const fetchMovieSeats = async () => {
            try {
                // Use Java endpoint or Python? Python is Query Service.
                // Python: GET /reservations/movies/{id}/seats
                const response = await axios.get(`${API_PYTHON_URL}/reservations/movies/${selectedMovie}/seats`);
                setMovieSeats(response.data);
            } catch (error) {
                console.error(error);
                setMovieSeats([]);
            } finally {
                setMovieLoading(false);
            }
        };
        fetchMovieSeats();
    }, [selectedMovie]);

    if (!username) {
        return (
            <Container size="lg" py="xl">
                <Text align="center" color="dimmed">Please log in to view reservations.</Text>
            </Container>
        );
    }

    return (
        <Container size="lg" py="xl">
            <Title order={1} align="center" mb={30} style={{ color: 'white', fontWeight: 700 }}>
                Reservation Center
            </Title>

            <Tabs defaultValue="my-reservations" variant="pills" radius="md">
                <Tabs.List position="center" mb="xl">
                    <Tabs.Tab value="my-reservations" icon={<IconUser size={14} />}>My Reservations</Tabs.Tab>
                    <Tabs.Tab value="seat-status" icon={<IconMovie size={14} />}>Check Seat Status</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="my-reservations" pt="xs">
                    <Text align="center" mb="md" color="dimmed">Logged in as: <b>{username}</b></Text>
                    {loading ? (
                        <Center><Loader color="blue" type="bars" /></Center>
                    ) : (
                        <>
                            {mySeats.length === 0 && (
                                <Text align="center" color="dimmed" size="lg">No reservations found for you.</Text>
                            )}

                            {/* Grouped Reservations */}
                            {(() => {
                                // Group seats by movie
                                const grouped = {};
                                mySeats.forEach(seat => {
                                    const movie = movies.find(m => m.value === seat.movie_id.toString());
                                    const title = movie ? movie.label : `Movie #${seat.movie_id}`;
                                    if (!grouped[title]) grouped[title] = [];
                                    grouped[title].push(seat);
                                });

                                return Object.entries(grouped).map(([title, activeSeats]) => (
                                    <Box key={title} mb={40}>
                                        <Title
                                            order={3}
                                            color="white"
                                            mb="md"
                                            style={{ borderBottom: '1px solid #333', paddingBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}
                                        >
                                            <IconMovie size={24} color="#0a84ff" />
                                            {title}
                                            <Badge color="gray" variant="outline" ml="auto">{activeSeats.length} Tickets</Badge>
                                        </Title>
                                        <SimpleGrid cols={{ base: 2, sm: 4, md: 6, lg: 8 }} spacing="sm">
                                            {activeSeats.map((seat) => (
                                                <Paper
                                                    key={seat.id}
                                                    p="sm"
                                                    radius="md"
                                                    style={{
                                                        backgroundColor: '#1c1c1e',
                                                        border: '1px solid #333',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        height: 100,
                                                        position: 'relative',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <Box
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: 4,
                                                            height: '100%',
                                                            backgroundColor: '#0a84ff'
                                                        }}
                                                    />
                                                    <IconArmchair size={24} color="#0a84ff" />
                                                    <Text size="xl" weight={800} mt={5} color="white" style={{ fontFamily: 'monospace' }}>
                                                        {seat.seat_number}
                                                    </Text>
                                                    <Badge size="xs" variant="filled" color="blue" mt={5}>Reserved</Badge>
                                                </Paper>
                                            ))}
                                        </SimpleGrid>
                                    </Box>
                                ));
                            })()}
                        </>
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="seat-status" pt="xs">
                    <Container size="xs" mb="xl">
                        <Select
                            label="Select Movie"
                            placeholder="Pick a movie to view status"
                            data={movies}
                            value={selectedMovie}
                            onChange={setSelectedMovie}
                            styles={{ label: { color: 'white' } }}
                        />
                    </Container>

                    {movieLoading ? (
                        <Center><Loader color="blue" /></Center>
                    ) : (
                        selectedMovie && (
                            <Box mt="xl">
                                <Text align="center" mb="md" color="dimmed">Live Seat Status (Read-Only)</Text>
                                <SeatGrid seats={movieSeats} readOnly={true} />
                                <Center mt="md">
                                    <Badge color="gray" variant="dot" mr="md">Reserved</Badge>
                                    <Badge color="dark" variant="outline">Available</Badge>
                                </Center>
                            </Box>
                        )
                    )}
                </Tabs.Panel>
            </Tabs>
        </Container>
    );
}

export default ReservationCheck;
